import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";
import { LoaderPinwheel } from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import ModalTipoDocumento from "../../components/modals/ModalTipoDocumento";

const TiposDocumentoPage = () => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const API_URL = `${process.env.REACT_APP_API_URL}/tipos-documento`;
  const token = localStorage.getItem("token");

  const fetchTipos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTipos(res.data || []);
    } catch (error) {
      toast.error("Error al obtener tipos de documento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTipos();
    // eslint-disable-next-line
  }, []);

  const filtered = tipos.filter((tipo) =>
    tipo.nombre.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSave = async (formData) => {
    try {
      if (selectedTipo) {
        await axios.put(`${API_URL}/${selectedTipo._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Tipo actualizado");
      } else {
        await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Tipo creado");
      }

      setShowModal(false);
      setSelectedTipo(null);
      fetchTipos();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error al guardar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este tipo?")) return;

    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Eliminado");
      fetchTipos();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <Card className="shadow-md">
      <CardContent>
        <ModalTipoDocumento
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedTipo(null);
          }}
          onSave={handleSave}
          entity={selectedTipo}
        />

        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
          <h2 className="text-lg font-semibold">Tipos de documento</h2>

          <div className="flex gap-2 w-full sm:w-auto">
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border rounded px-3 py-2 w-full sm:w-64"
            />

            {hasPermission("equipos.editar") && (
              <Button onClick={() => setShowModal(true)}>+ Nuevo</Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="mb-4 flex items-center gap-2 bg-blue-50 p-2 rounded text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin" />
            Cargando...
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded">
            <thead className="bg-gray-100 text-sm">
              <tr>
                <th className="px-4 py-2 text-left">Nombre</th>
                <th className="px-4 py-2 text-center">Obligatorio</th>
                <th className="px-4 py-2 text-center">Expedición</th>
                <th className="px-4 py-2 text-center">Vencimiento</th>
                <th className="px-4 py-2 text-right">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((tipo) => (
                <tr key={tipo._id} className="border-b">
                  <td className="px-4 py-2">{tipo.nombre}</td>
                  <td className="text-center">
                    {tipo.obligatorio ? "Sí" : "No"}
                  </td>
                  <td className="text-center">
                    {tipo.requiereFechaExpedicion ? "Sí" : "No"}
                  </td>
                  <td className="text-center">
                    {tipo.requiereFechaVencimiento ? "Sí" : "No"}
                  </td>
                  <td className="text-right px-4">
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedTipo(tipo);
                        setShowModal(true);
                      }}
                    >
                      Editar
                    </Button>

                    <Button
                      size="sm"
                      className="ml-2 bg-red-500 text-white"
                      onClick={() => handleDelete(tipo._id)}
                    >
                      Eliminar
                    </Button>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-gray-500">
                    Sin resultados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TiposDocumentoPage;
